import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import PaymentCheckoutFlow from '../components/PaymentCheckoutFlow';
import { academyAPI, tournamentParticipantAPI } from '../../services/api';
import { paymentsAPI } from '../services/PaymentApi';
import { AcademyThemeScope } from '../../contexts/ThemeContext';

/**
 * The checkout page. One address, no chrome.
 *
 *   /checkout?order=<uuid>&from=<path>&title=<label>
 *
 * Header, footer and the mobile tab bar are all suppressed for this path (see
 * PortalLayoutHider in App.jsx). Somebody part-way through paying should have
 * exactly two ways out — pay, or go back where they came from — and a nav bar
 * offering eight others is how a checkout loses people.
 *
 * `from` is what "back" means. It is carried in the URL rather than in router
 * state because this page is also Stripe's return destination: the browser
 * arrives here fresh, with no history to consult, and a state-only value would
 * be gone by exactly the moment it is needed.
 */
/**
 * Checkout keeps the branding of wherever the payer came from.
 *
 * An academy portal drops Kynaix's chrome so the venue's own identity stands
 * alone — and then checkout, being its own route, reverted to Kynaix's palette
 * mid-purchase. Paying is the moment branding matters most: a page that looks
 * like somebody else's is a page people abandon.
 *
 * The theme has to be established ABOVE the screen that reads it, so the page
 * is split the same way AcademyDetailsPage is: a shell that resolves the key,
 * and the page that renders inside it. Reached from Kynaix rather than an
 * academy, there is no key and the default palette applies untouched.
 */
/**
 * Remembered so a return from Stripe does not repaint.
 *
 * The theme is fetched, so the first paint has to guess — and guessing wrong
 * showed Kynaix's palette for a beat before the academy's arrived. Coming back
 * from stripe.com is a cold render of the whole app, which is exactly when that
 * flash is most visible and least explicable: the payer sees a different site
 * blink past on the way back from paying.
 *
 * sessionStorage rather than memory, because that render is a new page load.
 */
const THEME_CACHE = 'kynaix.checkout.themeKey';

const cachedThemeKey = (academyKey) => {
  if (!academyKey) return null;
  try {
    const raw = sessionStorage.getItem(THEME_CACHE);
    const map = raw ? JSON.parse(raw) : {};
    return map[academyKey] ?? null;
  } catch { return null; }
};

const rememberThemeKey = (academyKey, key) => {
  if (!academyKey) return;
  try {
    const raw = sessionStorage.getItem(THEME_CACHE);
    const map = raw ? JSON.parse(raw) : {};
    map[academyKey] = key ?? null;
    sessionStorage.setItem(THEME_CACHE, JSON.stringify(map));
  } catch { /* private mode — one repaint is survivable */ }
};

export default function PaymentCheckoutPage() {
  const [params] = useSearchParams();
  const academyKey = params.get('academy');

  // Seeded from the cache, so a warm return paints correctly the first time.
  const [themeKey, setThemeKey] = useState(() => cachedThemeKey(academyKey));
  /**
   * Whether this academy takes cards at all.
   *
   * Read from the academy the theme fetch already loads, so it costs nothing
   * extra. Undefined until that resolves — treated as allowed, because the
   * common case is that cards are on and the server refuses one anyway if they
   * are not. What must not happen is offering a card, taking the payer to
   * Stripe, and being turned away there.
   */
  const [allowCard, setAllowCard] = useState(true);
  const [resolved, setResolved] = useState(() => !academyKey || cachedThemeKey(academyKey) !== null);

  useEffect(() => {
    if (!academyKey) { setResolved(true); return; }
    let live = true;
    academyAPI.getById(academyKey)
      .then(a => {
        if (!live) return;
        setThemeKey(a?.themeKey || null);
        rememberThemeKey(academyKey, a?.themeKey || null);
        // Off removes the card option at checkout — cash, check and Venmo at
        // the counter only.
        setAllowCard(a?.features?.enableOnlineBooking !== false);
      })
      .catch(() => {})
      .finally(() => { if (live) setResolved(true); });
    return () => { live = false; };
  }, [academyKey]);

  // Nothing is drawn until the palette is known. A blank beat is better than
  // the wrong brand: the alternative is painting Kynaix's colours over an
  // academy's checkout and then swapping them under the payer.
  if (!resolved) return <div className="min-h-screen" />;

  return (
    <AcademyThemeScope themeKey={themeKey}>
      <PaymentCheckoutPageInner allowCard={allowCard} />
    </AcademyThemeScope>
  );
}

function PaymentCheckoutPageInner({ allowCard = true }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  /**
   * One id, or several separated by commas.
   *
   * Several when a payer registered more than one entrant: each is its own
   * order, and they are paid together. The questions and the agreement are
   * looked up from the first, because every order in one basket is for the
   * same offering — a tournament's entrants all enter the same tournament.
   */
  const orderIds = (params.get('order') || '').split(',').map(v => v.trim()).filter(Boolean);
  const orderId = orderIds[0] || null;
  /* Whether Book RAISED this order or merely reopened one that already existed.
   * It decides two things: whether backing out may withdraw the order (see
   * goBack), and whether the place it holds counts as a prior booking (below).
   *
   * Both are stated rather than one inferred from the other's absence. Checkout
   * is also reached from My Orders and from Stripe's return, where neither is
   * true and "no answer" must not read as "this press just made it". */
  const reused = params.get('reused') === '1';
  const raised = params.get('raised') === '1';
  const title   = params.get('title') || 'Checkout';
  // Who this is FOR. Falls back to the payer, who is the registrant whenever
  // nobody said otherwise.
  const payingFor = params.get('for');
  /**
   * Where a COMPLETED payment lands, when that differs from where cancelling
   * lands. Giving up belongs back where the decision was made; paying belongs
   * wherever the thing just bought now appears. Same in-app-path rule as
   * `from`, for the same open-redirect reason.
   */
  const doneTo = useMemo(() => {
    const raw = params.get('done');
    return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
  }, [params]);
  const academyKey = params.get('academy');

  /**
   * The seller's own questions, found rather than passed.
   *
   * Which questions to ask belongs to what is being bought, and the order
   * already says what that is — so the page reads them off the order instead
   * of every caller having to remember to send them. A caller that must
   * remember is a caller that will forget, and the questions would then be
   * silently skipped for whichever flow was written last.
   */
  const [infoFields, setInfoFields] = useState([]);
  const [askedBy, setAskedBy] = useState(null);
  /**
   * Whether to ask who this is for, and who is already spoken for.
   *
   * Only open play and programmes choose a registrant at checkout. A court has
   * nobody to choose — an hour is an hour — and a tournament has already
   * collected its entrants on its own form, so asking again there would offer
   * to change something already decided.
   */
  const [subjectType, setSubjectType] = useState(null);
  const [subjectId, setSubjectId] = useState(null);
  const [takenIds, setTakenIds] = useState([]);
  const [provisionalIds, setProvisionalIds] = useState([]);
  const [agreement, setAgreement] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    (async () => {
      const order = await paymentsAPI.getOrder(orderId).catch(() => null);
      const item = (order?.items || [])[0];
      if (!item) return;

      // One call, whatever was bought. The questions are keyed by the same
      // (subjectType, subjectId) the order already carries, so there is nothing
      // to look up the offering itself for.
      const res = await paymentsAPI
        .offeringQuestions(item.subjectType, item.subjectId)
        .catch(() => null);

      const signable = await paymentsAPI
        .offeringAgreements(item.subjectType, item.subjectId)
        .catch(() => null);

      const who = ['session', 'program'].includes(item.subjectType)
        ? await paymentsAPI.offeringRegistrants(item.subjectType, item.subjectId, orderId)
          .catch(() => null)
        : null;

      // The only guard that earns its place: everything above merely returns,
      // and it is setting state after an unmount that React objects to.
      if (cancelled) return;
      setSubjectType(item.subjectType);
      setSubjectId(item.subjectId);
      if (signable) setAgreement(signable);
      if (res) { setInfoFields(res.fields || []); setAskedBy(res.askedBy || null); }
      /* WHO HOLDS A PLACE — and which of those places this order is holding.
       *
       * `taken` is the whole truth and is what greys people out. Hiding the
       * payer from it was the first cure tried and it made the screen lie: the
       * session list said "You booked" while checkout offered to book them
       * again, with nothing admitting they already had a seat.
       *
       * `provisional` is the narrower fact that makes the honest version work —
       * places held only by THIS order. The payer is normally in it, because
       * Book takes the seat before checkout asks who it is for, and a seat this
       * order is holding is not a rival booking: it is the thing being paid
       * for. So it may stay chosen while still showing as taken. */
      if (who) {
        /* ⚠️ AND A SEAT THIS PRESS JUST TOOK IS NOT A BOOKING YOU ALREADY HAD.
         *
         * Book holds the place before checkout asks who it is for, so the payer
         * lands in `taken` on EVERY session they press Book on — and reported
         * plainly the picker greys them out on a night they have never booked,
         * saying "already booked" about a seat two seconds old.
         *
         * `raised` is the press that made it. On that press the order's own
         * provisional places are dropped: nothing about them predates this
         * checkout, so there is nothing to warn anybody about. On a reopened
         * order they stay, because then the place really is one the payer
         * already had — which is the case the grey exists for. */
        const mine = new Set((who.provisional || []).map(String));
        setTakenIds(raised
          ? (who.taken || []).filter(id => !mine.has(String(id)))
          : (who.taken || []));
        setProvisionalIds(who.provisional || []);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, raised]);
  // A tournament entry or a roster place: paid as a registration, not an order.
  const kind    = params.get('kind') || 'order';
  const ids     = (params.get('ids') || '').split(',').filter(Boolean);
  const amount  = params.get('amount');

  // Only ever an in-app path. An absolute URL here would make this page an
  // open redirect — anyone could send a payer to checkout and bounce them to
  // a site of their choosing the moment they finished paying.
  const from = useMemo(() => {
    const raw = params.get('from');
    return raw && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
  }, [params]);

  /**
   * Walking away gives the place back.
   *
   * A place is held before payment so the order exists for this page to be
   * pointed at, which means abandoning checkout must undo it — otherwise the
   * seat stays taken by somebody who decided not to buy. Only on an EXPLICIT
   * cancel: coming back from Stripe unpaid is a payer mid-attempt, and pulling
   * their place out from under them is how a retry becomes impossible.
   *
   * `release` names what to give back rather than passing a function, because
   * this page is also Stripe's return destination and arrives with no state.
   */
  const release = params.get('release');
  const goBack = useCallback(async () => {
    const [kind, id, playerId] = (release || '').split(':');

    /* WITHDRAW THE UNPAID ORDER, not just the place it was holding.
     *
     * Each branch below gives back one kind of place, and none of them closed
     * the order — so abandoning checkout left a pending order behind for ever.
     * That is not just untidy: a pending order on a session BLOCKS BOOKING THAT
     * SESSION AGAIN, so the payer who thought better of paying could not come
     * back later and book it. The API's own cancelOrder says the same thing
     * from the other side: "an abandoned pending order is a seat held for free".
     *
     * cancelOrder releases every kind of place itself, through the same planner
     * the refund path uses, so when it succeeds the branches below have nothing
     * left to do and are skipped — which also stops them reporting a failure to
     * release a place that has already gone.
     *
     * Only reached on the abandon path. Choosing to pay at the desk leaves
     * through goBackKeepingPlace, and that order is meant to survive: it is
     * exactly what the desk is going to settle.
     */
    /* NEVER CANCEL A PLACE THAT IS ALREADY HELD.
     *
     * Leaving checkout withdraws the order, which is right for somebody who
     * thought better of paying — an abandoned pending order is a seat held for
     * free, and on a session it blocks that session being booked again.
     *
     * It is wrong once the place is real. Choosing "pay at the desk" reserves
     * the seat and leaves through goBackKeepingPlace, but the payer can walk
     * BACK to the payment step afterwards, and "Cancel and go back" sitting
     * under the method list then destroyed a booking they had just made. That
     * happened: a cash booking was confirmed and cancelled twenty-two seconds
     * later, with the order recorded as cancelled by the desk because
     * cancelOrder stamps `source: 'desk'` whoever calls it.
     *
     * A settled order is not ours to withdraw. Asked of the order rather than
     * of the screen's own state, because the screen can be re-entered and the
     * order cannot lie about what it is.
     */
    /* ⚠️ AN ORDER THIS CHECKOUT ONLY REOPENED IS NOT ITS TO WITHDRAW.
     *
     * The guard below asks whether the order is settled, which is not the same
     * question and misses the case that actually cost somebody their place: a
     * PENDING order that already existed. One live order per person per thing
     * means Book on a night already booked hands back the existing order, and
     * "Reserve — pay at the desk" leaves it pending too — so the payer walked
     * into checkout to look at a place they had, pressed "Cancel and go back",
     * and the order was cancelled and the seat released. No error, no warning,
     * and the list simply stopped saying "You booked".
     *
     * The screen that raised the order is the only thing that knows, so it says
     * so in the URL. Absent means what it always meant: this checkout raised
     * the order and may take it back. */
    if (reused) {
      goBackKeepingPlace();
      return;
    }

    const settledAlready = await Promise.all(
      orderIds.map(oid => paymentsAPI.getOrder(oid)
        .then(o => o?.status && o.status !== 'pending')
        .catch(() => false)),
    );
    if (settledAlready.some(Boolean)) {
      goBackKeepingPlace();
      return;
    }

    let withdrawn = false;
    if (orderIds.length) {
      const results = [];
      for (const oid of orderIds) {
        // Sequential, like the withdrawals below: these can share a charge.
        // eslint-disable-next-line no-await-in-loop
        results.push(await paymentsAPI.cancelOrder(oid).then(() => true).catch(() => false));
      }
      withdrawn = results.every(Boolean);
    }

    if (withdrawn) {
      // The entry is gone, and saying so is the whole point of the tournament
      // branch below — which this path skips, so the message is repeated here
      // rather than lost. Somebody who is silently un-registered reads it as
      // the registration having failed, and tries again.
      if (kind === 'tournament' && id) {
        const n = id.split(',').filter(Boolean).length;
        toast(n > 1
          ? `${n} entries were not kept — the fee was not paid. Register again when you are ready.`
          : 'Your entry was not kept — the fee was not paid. Register again when you are ready.',
        { icon: 'ℹ️' });
      }
      if (from) navigate(from); else navigate(-1);
      return;
    }


    // A tournament entry is created before payment, the same way an open play
    // place is held before payment — so abandoning checkout has to undo it.
    // Without this the payer cancelled at Stripe, came back, and found
    // themselves registered for a tournament they had not paid for.
    if (kind === 'tournament' && id) {
      const ids = id.split(',').filter(Boolean);
      // Sequential, like every other withdrawal: these share a charge, and
      // firing them together races on it.
      let released = 0;
      for (const rid of ids) {
        // eslint-disable-next-line no-await-in-loop
        const ok = await tournamentParticipantAPI.withdrawRegistration(rid)
          .then(() => true).catch(() => false);
        if (ok) released += 1;
      }

      // AND SAY SO. The court and session branches below both tell the payer
      // what became of their place; this one undid the entry in silence. So
      // somebody ticked Player, was sent to checkout, thought better of paying,
      // and came back to a Register tab that showed them as not entered — with
      // nothing anywhere explaining that leaving had taken the entry away. It
      // reads as the registration having failed, and they try again, and it
      // happens again.
      if (released > 0) {
        toast(released > 1
          ? `${released} entries were not kept — the fee was not paid. Register again when you are ready.`
          : 'Your entry was not kept — the fee was not paid. Register again when you are ready.',
        { icon: 'ℹ️' });
      }
    }

    // A court hour, held before payment the same way a session place is.
    if (kind === 'court') {
      const [academyId, courtId, bookingId] = [id, playerId, (release || '').split(':')[3]];
      if (academyId && courtId && bookingId) {
        await academyAPI.courts.cancelBooking(academyId, courtId, bookingId)
          .catch((err) => {
            toast(err?.message || 'Your court is still booked — cancel it from your bookings.',
              { icon: 'ℹ️' });
          });
      }
      if (from) navigate(from); else navigate(-1);
      return;
    }

    if (kind === 'session' && id) {
      // A place that cannot be released must not be reported as released. The
      // commonest reason is the honest one: the order has already been paid,
      // and giving that place back is a refund, not a cancellation. Leaving
      // still means leaving — the payer is never trapped here — but they are
      // told the booking stands, rather than walking away believing otherwise.
      await academyAPI.sessions
        .cancelBooking(id, playerId ? { playerId } : {})
        .catch((err) => {
          toast(err?.message || 'Your place is still booked — cancel it from your bookings.',
            { icon: 'ℹ️' });
        });
    }
    if (from) navigate(from);
    else navigate(-1);
    // orderIds comes from the URL on every render, so it is a new array each
    // time; joined so an unchanged list does not rebuild this callback.
    // doneTo and from are what goBackKeepingPlace reads; naming them keeps this
    // honest rather than relying on the function identity being stable.
  }, [from, doneTo, navigate, release, reused, orderIds.join(',')]);

  /** After paying there is nothing to release — the place is theirs. */
  const goBackKeepingPlace = useCallback(() => {
    if (doneTo) navigate(doneTo);
    else if (from) navigate(from);
    else navigate(-1);
  }, [doneTo, from, navigate]);

  const goHome = useCallback(() => navigate('/'), [navigate]);

  if (!orderIds.length && ids.length === 0) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-6 ${theme.background}`}>
        <div className="text-center space-y-3">
          <p className={`text-sm ${theme.textSecondary}`}>There is nothing to pay for here.</p>
          <button onClick={goHome} className={`text-sm ${theme.textMuted} hover:underline`}>Go home</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background}`}>
      <div className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
        <PaymentCheckoutFlow
          orderIds={orderIds}
          kind={kind}
          registrationIds={ids.length ? ids : null}
          amount={amount}
          title={title}
          user={payingFor ? { name: payingFor } : user}
          // Step one, always: who this is for. The caller can still name them
          // up front via ?for=, which pre-fills the answer rather than skipping
          // the question — a payer buying for someone else should see who.
          allowCard={allowCard}
          collectRegistrant={['session', 'program'].includes(subjectType)}
          takenIds={takenIds}
          provisionalIds={provisionalIds}
          agreement={agreement}
          subjectType={subjectType}
          subjectId={subjectId}
          infoFields={infoFields}
          infoAskedBy={askedBy}
          backTo={from ? 'where you were' : null}
          onBack={goBack}
          onBackAfterPaying={goBackKeepingPlace}
          onHome={goHome}
          /**
           * Nothing to reload here — this page owns no list. The screens that
           * send payers here re-read their own data when they are navigated
           * back to, which is what makes this page reusable rather than
           * something each caller has to teach about its own data.
           */
          onSuccess={() => {}}
          onFailure={() => {}}
        />
      </div>
    </div>
  );
}
